import { useEffect, useRef, useState } from "react";
import { readConfig } from "../../../lib/config";
import type { LiveWindow } from "./useMicrobitLive";

export const MB_AI_OUT = "field.mbAi";
export const MB_AI_CLASSES_IN = "bench.mbAiClasses";
export type AiVia = "camera" | "card";

/**
 * 놀이터 AI 카드(PICNIC-MB-FIELD-001 §6 3단계) — 왼쪽 칸(Bench 시뮬레이터)과 주고받는다.
 *  - 받기: Bench 가 알려 주는 아이 프로그램의 클래스 이름(`bench.mbAiClasses` — '내 모델이 ⟨사과⟩로 보이면' 의 이름들). 설정된 Bench 출처 · 칸의 창에서 온 것만.
 *  - 보내기: 진짜 카메라 · 가상 카드가 고른 클래스(`field.mbAi {klass, confidence, via}`)를 칸의 창으로(Bench 출처로만) — Bench 가 시뮬레이터 확장 채널로 넣는다.
 */
export function useAiBridge(simWin: { readonly current: Window | null }, opts: { win?: LiveWindow; benchOrigin?: string | null } = {}) {
  const win: LiveWindow = opts.win ?? window;
  const benchOrigin = opts.benchOrigin === undefined ? readConfig().benchOrigin : opts.benchOrigin;
  const [classes, setClasses] = useState<string[]>([]);
  /** 아이 프로그램의 라디오 그룹(Bench 가 main.ts 의 radio.setGroup 에서 읽어 같은 소식에 싣는다 · 없으면 null — 핫앤콜드 보물찾기) */
  const [radioGroup, setRadioGroup] = useState<number | null>(null);
  const last = useRef<{ klass: string; at: number } | null>(null);

  useEffect(() => {
    if (!benchOrigin) return;
    const h = (ev: MessageEvent) => {
      if (ev.origin !== benchOrigin || (simWin.current && ev.source !== simWin.current)) return;
      const d = ev.data as { type?: unknown; v?: unknown; classes?: unknown; radioGroup?: unknown } | null;
      if (!d || d.type !== MB_AI_CLASSES_IN || d.v !== 1 || !Array.isArray(d.classes)) return;
      setRadioGroup(Number.isInteger(d.radioGroup) && (d.radioGroup as number) >= 0 && (d.radioGroup as number) <= 255 ? (d.radioGroup as number) : null);
      setClasses(d.classes.filter((c): c is string => typeof c === "string" && c.trim().length > 0).map((c) => c.trim().slice(0, 40)).slice(0, 16));
    };
    win.addEventListener("message", h);
    return () => win.removeEventListener("message", h);
  }, [win, benchOrigin, simWin]);

  /** 클래스 하나를 보드로 — 빈 이름은 '모르겠음'(보내지 않는다) */
  const send = (klass: string, confidence: number, via: AiVia) => {
    const k = klass.trim();
    if (!k || !benchOrigin || !simWin.current) return false;
    last.current = { klass: k, at: Date.now() };
    try { simWin.current.postMessage({ type: MB_AI_OUT, v: 1, klass: k, confidence: Math.round(Math.max(0, Math.min(100, confidence))), via }, benchOrigin); } catch { return false; }
    return true;
  };
  return { classes, radioGroup, send, ready: !!benchOrigin };
}
